import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { AUDITED, SERVICE_TABLE_LABEL, SOURCE_LABEL, ACTION_LABEL, changeLines, historyScopeText, offlineNote } from '../lib/auditCore'

// Small "History" link on a record: opens the changes of that one record (audit_log, read rules decide what is shown).
export function canSeeAllHistory({ accountType, isCaptain, can }) {
  return accountType !== 'crew' || isCaptain || (can && can('audit', 'full'))
}

export default function HistoryLink({ table, rowId, label, style }) {
  const [open, setOpen] = useState(false)
  if (!table || !rowId || !(AUDITED.has(table) || SERVICE_TABLE_LABEL[table])) return null
  return (
    <>
      <button type="button" onClick={e => { e.stopPropagation(); setOpen(true) }} title="Who changed this record, and when"
        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--gold2)', fontSize: 11.5, cursor: 'pointer', textDecoration: 'underline', ...style }}>
        History
      </button>
      {open && <HistoryModal table={table} rowId={rowId} label={label} onClose={() => setOpen(false)} />}
    </>
  )
}

function HistoryModal({ table, rowId, label, onClose }) {
  const auth = useAuth()
  const all = canSeeAllHistory(auth)
  const [rows, setRows] = useState(null)
  const [err, setErr] = useState('')
  const [assigned, setAssigned] = useState(false)   // assigned to me: the read rule shows everyone's changes of this record
  useEffect(() => {
    let live = true
    if (!all) supabase.rpc('audit_assigned_to_me', { p_table: table, p_row: String(rowId) }).then(({ data }) => { if (live) setAssigned(data === true) })
    return () => { live = false }
  }, [all, table, rowId])
  useEffect(() => {
    let live = true
    supabase.from('audit_log').select('*')   // * = also the approval comment (sql/quick-wins.sql) once it exists
   
      .eq('table_name', table).eq('row_id', String(rowId)).order('at', { ascending: false }).limit(200)
      .then(({ data, error }) => {
        if (!live) return
        if (error) setErr(/audit_log/.test(error.message || '') ? 'The change history is not set up yet (sql/audit-log.sql).' : error.message)
        setRows(data || [])
      })
    return () => { live = false }
  }, [table, rowId])
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  return (
    <div onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) onClose() }} style={{ position: 'fixed', inset: 0, background: 'rgba(4,10,20,.8)', zIndex: 3100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--ink2)', border: '0.5px solid var(--border2)', borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '86vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 18px 12px', borderBottom: '0.5px solid var(--border)' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>History{label ? ' — ' + label : ''}</div>
            {!all && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{historyScopeText(table, assigned)}</div>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14 }}>{'✕'}</button>
        </div>
        <div style={{ padding: '10px 18px 16px', overflowY: 'auto' }}>
          {err && <div style={{ color: '#b4313c', fontSize: 12.5 }}>{err}</div>}
          {rows === null && !err && <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Loading&hellip;</div>}
          {rows && !rows.length && !err && <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>No recorded changes yet. Changes are recorded from the day the change history was switched on.</div>}
          {(rows || []).map(e => <HistoryEntry key={e.id} e={e} />)}
        </div>
      </div>
    </div>
  )
}

export function HistoryEntry({ e, showRecord }) {
  const lines = changeLines(e)
  return (
    <div style={{ padding: '9px 0', borderBottom: '0.5px solid var(--border)', fontSize: 12.5 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{new Date(e.at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
        <span style={{ fontWeight: 600, color: 'var(--text)' }}>{e.user_name || 'Unknown user'}</span>
        {e.source !== 'app' && <span style={{ fontSize: 10.5, padding: '1px 7px', borderRadius: 10, background: e.source === 'ai' ? 'rgba(52,120,181,.15)' : 'rgba(128,128,128,.15)', color: e.source === 'ai' ? '#2a6a96' : 'var(--muted)' }}>{SOURCE_LABEL[e.source] || e.source}</span>}
        <span style={{ color: e.action === 'delete' ? '#b4313c' : e.action === 'insert' ? '#137050' : 'var(--gold2)' }}>{ACTION_LABEL[e.action] || e.action}</span>
        {showRecord}
      </div>
      {e.action !== 'insert' && lines.map(l => <div key={l.field} style={{ color: 'var(--text)', marginTop: 3, paddingLeft: 2, wordBreak: 'break-word' }}>{l.text}</div>)}
      {e.action === 'insert' && lines.length > 0 && <div style={{ color: 'var(--muted)', marginTop: 3, wordBreak: 'break-word' }}>{lines.slice(0, 6).map(l => l.text).join(' · ')}{lines.length > 6 ? ' …' : ''}</div>}
      {e.offline_at && <div style={{ marginTop: 3, paddingLeft: 2, fontSize: 11.5, color: 'var(--muted)' }}>{'\u23f3 ' + offlineNote(e)}</div>}
      {e.comment && <div style={{ marginTop: 3, paddingLeft: 2, fontStyle: 'italic', color: 'var(--text)', wordBreak: 'break-word' }}>{'Comment: ' + e.comment}</div>}
    </div>
  )
}
